import { LoadingSpinner, Typography } from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { useCreateMatterFile } from "@/lib/data/react-query/matters/matter-file";
import type { MatterFile } from "@/lib/types";
import { getMatterFileNamesInFolder } from "@/lib/utils/matterFile";
import { useQuery } from "@tanstack/react-query";
import { CreateDocxSlideover } from "./CreateDocxSlideover";

interface CreateDocxFileSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  matterId: string;
  currentFolderId: string;
  afterCreateFile?: (file: MatterFile) => Promise<void> | void;
}

const CREATE_FILE_MODAL_CLASSNAME =
  "top-0 left-0 h-[100dvh] !max-h-[100dvh] !w-screen !max-w-none translate-x-0 translate-y-0 rounded-none";

export const CreateDocxFileSlideover = ({
  open,
  setOpen,
  matterId,
  currentFolderId,
  afterCreateFile,
}: CreateDocxFileSlideoverProps) => {
  const {
    data: matterData,
    isLoading: isMatterLoading,
    isError: isMatterError,
  } = useQuery({
    ...retrieveMatterQueryOptions(matterId),
    enabled: open,
  });
  const matter = matterData?.data;
  const createMatterFile = useCreateMatterFile();
  const existingFileNames = getMatterFileNamesInFolder(
    matter?.files ?? [],
    currentFolderId,
  );
  const showLoadingState = isMatterLoading && !matter;
  const showErrorState = isMatterError || (!showLoadingState && !matter);

  if (showLoadingState || showErrorState) {
    return (
      <AppModal
        open={open}
        onOpenChange={setOpen}
        title="Create file"
        description="Create a new document."
        bodyContained
        className={CREATE_FILE_MODAL_CLASSNAME}
      >
        <div className="flex min-h-0 flex-1 items-center justify-center px-6 py-4">
          {showLoadingState ? (
            <LoadingSpinner className="h-6 w-6" />
          ) : (
            <Typography className="text-destructive text-sm">
              Unable to load matter data for this document.
            </Typography>
          )}
        </div>
      </AppModal>
    );
  }

  return (
    <CreateDocxSlideover
      open={open}
      setOpen={setOpen}
      title="Create file"
      description="Create a new document."
      fileType="matter-file"
      initialFileName="New document"
      existingFileNames={existingFileNames}
      onSave={async (file, normalizedFileName) => {
        const result = await createMatterFile.mutateAsync({
          matterId,
          isFolder: false,
          idLocation: currentFolderId,
          name: normalizedFileName,
          file,
        });

        if (afterCreateFile) {
          await afterCreateFile(result.data);
        }
      }}
    />
  );
};
